/**
 * Utility functions for user operations
 */
import { supabase } from '../supabase';

/**
 * Generate a secure unique token for edit links
 * @returns {string} A random alphanumeric token
 */
export const generateUniqueToken = () => {
  return Math.random().toString(36).substring(2, 15) + 
         Math.random().toString(36).substring(2, 15);
};

/**
 * Creates an edit link URL
 * @param {string} token - The edit token
 * @param {string} venueId - Optional venue ID
 * @returns {string} The complete edit link URL
 */
export const createEditLink = (token, venueId = null) => {
  const baseUrl = window.location.origin;
  const venueParam = venueId ? `?venue_id=${venueId}` : '';
  return `${baseUrl}/register/edit/${token}${venueParam}`;
};

/**
 * Loads a user by their edit token
 * @param {string} token - The edit token
 * @returns {Promise<Object>} Object containing user data and any error
 */
export const loadUserByToken = async (token) => {
  try {
    const { data, error } = await supabase
      .from('users')
      .select('*')
      .eq('edit_token', token)
      .limit(1);

    if (error) throw error;

    if (data && data.length > 0) {
      return { user: data[0], error: null };
    } else {
      return { user: null, error: 'Ongeldige of verlopen link.' };
    }
  } catch (err) {
    return { user: null, error: err.message };
  }
};

/**
 * Maps form data to database fields for user operations
 * @param {Object} formData - Form data from React Hook Form
 * @param {Object} options - Additional options
 * @returns {Object} Formatted user data for database
 */
export const mapFormToUserData = (formData, options = {}) => {
  const { 
    sessionId, 
    optInStats, 
    optInVideo, 
    optInSocials, 
    editToken = null,
    tagMappings = {},
    venueId = null
  } = options;

  // Get the actual tag ID if user entered a display name
  const actualTag = tagMappings[formData.mylapsTag] || formData.mylapsTag;

  return {
    first_name: formData.firstName,
    last_name: formData.lastName,
    email: formData.email,
    tag: actualTag,
    session_id: sessionId,
    opt_in_stats: optInStats,
    opt_in_video: optInVideo,
    opt_in_socials: optInSocials,
    alias: formData.alias,
    instagram_handle: formData.instagram_handle || null,
    birth_date: formData.birthDate || null,
    venue_id: venueId,
    ...(editToken && { edit_token: editToken })
  };
};

/**
 * Save user data to the database
 * @param {Object} userData - User data to save
 * @param {string|null} userId - User ID for updates, null for new users
 * @returns {Promise<Object>} Result of the operation
 */
export const saveUserData = async (userData, userId = null) => {
  try {
    let result;
    
    if (userId) {
      // Update existing record
      const { data, error } = await supabase
        .from('users')
        .update(userData)
        .eq('id', userId)
        .select();
      
      result = { data, error };
    } else {
      // Insert new record
      userData.created_at = new Date().toISOString();
      const { data, error } = await supabase
        .from('users')
        .insert([userData])
        .select();
        
      result = { data, error };
    }

    if (result.error) throw result.error;
    
    return { data: result.data, error: null };
  } catch (err) {
    return { data: null, error: err.message };
  }
}; 